import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  FormHelperText,
  Switch,
} from '@chakra-ui/react'
import {
  createUserSchema,
  updateUserSchema,
  type CreateUserFormData,
  type UpdateUserFormData,
} from '@/lib/validations/auth'
import { PasswordGenerator } from '@/components/PasswordGenerator'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import ReactSelect from 'react-select'

interface Role {
  id: number
  name: string
}

interface ApproverOption {
  id: number
  label: string
}

interface UserFormProps {
  userId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type CatalogDepartment = { id: number; name: string; code?: string }

type DepartmentSelectOption = {
  value: number
  label: string
  catalog: CatalogDepartment
}

type DepartmentApiRow = {
  id: number
  name: string
  code?: string
}

type CatalogDivision = { id: number; name: string; code?: string }

type DivisionSelectOption = {
  value: number
  label: string
  catalog: CatalogDivision
}

type DivisionApiRow = {
  id: number
  name: string
  code?: string
}

export function UserForm({ userId, onSuccess, onCancel }: UserFormProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [approvers, setApprovers] = useState<ApproverOption[]>([])
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userStatus, setUserStatus] = useState<number>(1)
  const [departmentCatalogList, setDepartmentCatalogList] = useState<CatalogDepartment[]>([])
  const [departmentSearchQuery, setDepartmentSearchQuery] = useState('')
  const [departmentSearching, setDepartmentSearching] = useState(false)
  const [divisionCatalogList, setDivisionCatalogList] = useState<CatalogDivision[]>([])
  const [divisionSearchQuery, setDivisionSearchQuery] = useState('')
  const [divisionSearching, setDivisionSearching] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!userId

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateUserFormData | UpdateUserFormData>({
    resolver: zodResolver(isEditMode ? updateUserSchema : createUserSchema),
    defaultValues: {
      user_status: 1,
      can_view_all: false,
    },
  })

  const passwordValue = watch('user_pass')

  const fetchDepartments = useCallback(
    async (search: string) => {
      setDepartmentSearching(true)
      try {
        const params: Record<string, string | number> = { per_page: 100 }
        if (search.trim()) params.search = search.trim()
        const { data } = await apiClient.get('/departments', { params })
        const raw = data?.data ?? data
        const list: DepartmentApiRow[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data)
                      ? raw.data
                      : []
        const mapped: CatalogDepartment[] = list
                    .filter((row) => typeof row.name === 'string' && row.name.trim().length > 0)
                    .map((row) => ({
                      id: Number(row.id),
                      name: String(row.name).trim(),
                      code: row.code != null && String(row.code).trim() !== '' ? String(row.code).trim() : undefined,
                    }))
          .filter((row) => row.name.length > 0)
        setDepartmentCatalogList(mapped)
      } catch (error: unknown) {
        showError(error as Error)
        setDepartmentCatalogList([])
      } finally {
        setDepartmentSearching(false)
      }
    },
    [showError]
  )

  useEffect(() => {
    const delay = departmentSearchQuery.trim() === '' ? 0 : 300
    const t = setTimeout(() => {
      void fetchDepartments(departmentSearchQuery)
    }, delay)
    return () => clearTimeout(t)
  }, [departmentSearchQuery, fetchDepartments])

  const departmentSelectOptions: DepartmentSelectOption[] = useMemo(
    () =>
      departmentCatalogList.map((d) => ({
        value: d.id,
        label: d.code ? `${d.name} (${d.code})` : d.name,
        catalog: d,
      })),
    [departmentCatalogList]
  )

  const fetchDivisions = useCallback(
    async (search: string) => {
      setDivisionSearching(true)
      try {
        const params: Record<string, string | number> = { per_page: 100 }
        if (search.trim()) params.search = search.trim()
        const { data } = await apiClient.get('/division-management/divisions', { params })
        const raw = data?.data ?? data
        const list: DivisionApiRow[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data)
                      ? raw.data
                      : []
        const mapped: CatalogDivision[] = list
                    .filter((row) => typeof row.name === 'string' && row.name.trim().length > 0)
                    .map((row) => ({
                      id: Number(row.id),
                      name: String(row.name).trim(),
                      code: row.code != null && String(row.code).trim() !== '' ? String(row.code).trim() : undefined,
                    }))
          .filter((row) => row.name.length > 0)
        setDivisionCatalogList(mapped)
      } catch (error: unknown) {
        showError(error as Error)
        setDivisionCatalogList([])
      } finally {
        setDivisionSearching(false)
      }
    },
    [showError]
  )

  useEffect(() => {
    const delay = divisionSearchQuery.trim() === '' ? 0 : 300
    const t = setTimeout(() => {
      void fetchDivisions(divisionSearchQuery)
    }, delay)
    return () => clearTimeout(t)
  }, [divisionSearchQuery, fetchDivisions])

  const divisionSelectOptions: DivisionSelectOption[] = useMemo(
    () =>
      divisionCatalogList.map((d) => ({
        value: d.id,
        label: d.code ? `${d.name} (${d.code})` : d.name,
        catalog: d,
      })),
    [divisionCatalogList]
  )

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient.get('/options/roles')
        const rolesData = Array.isArray(response.data) ? response.data : response.data.data || []
        const filteredRoles = rolesData.filter((role: Role) => role.name !== 'Developer Account')
        setRoles(filteredRoles)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingRoles(false)
      }
    }
    fetchRoles()
  }, [showError])

  useEffect(() => {
    const fetchApprovers = async () => {
      try {
        const { data } = await apiClient.get('/user-management/users/approvers', {
          params: { per_page: 1000 },
        })

        const raw = data?.data ?? data
        const users = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []

        const mapped: ApproverOption[] = users.map((u: any): ApproverOption => {
          const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim()
          return {
            id: Number(u.id),
            label: fullName || u.user_login || u.user_email || `User #${u.id}`,
          }
        })

        const unique: ApproverOption[] = Array.from(
          new Map<number, ApproverOption>(mapped.map((opt) => [opt.id, opt])).values()
        ).sort((a: ApproverOption, b: ApproverOption) => a.label.localeCompare(b.label))

        setApprovers(unique)
      } catch (error: any) {
        showError(error)
      }
    }
    fetchApprovers()
  }, [showError])

  useEffect(() => {
    if (isEditMode && userId) {
      const fetchUser = async () => {
        try {
          const response = await apiClient.get(`/user-management/users/${userId}`)
          const user = response.data?.data || response.data || {}

          let userRole = ''
          if (user.user_role) {
            if (typeof user.user_role === 'string') {
              try {
                const parsed = JSON.parse(user.user_role)
                userRole = parsed.id ? parsed.id.toString() : ''
              } catch {
                if (roles.length > 0) {
                  const role = roles.find(
                    (r) => r.name === user.user_role || r.id.toString() === user.user_role
                  )
                  userRole = role ? role.id.toString() : ''
                } else {
                  userRole = user.role_id ? user.role_id.toString() : ''
                }
              }
            } else if (user.user_role?.id) {
              userRole = user.user_role.id.toString()
            }
          } else if (user.role_id) {
            userRole = user.role_id.toString()
          }

          let statusValue = 1
          if (user.user_status !== undefined && user.user_status !== null) {
            if (typeof user.user_status === 'string') {
              if (user.user_status === 'Inactive' || user.user_status === '0') {
                statusValue = 0
              } else if (user.user_status === 'Suspended' || user.user_status === '2') {
                statusValue = 2
              } else if (user.user_status === 'Active' || user.user_status === '1') {
                statusValue = 1
              }
            } else {
              statusValue = Number(user.user_status)
            }
          }

          const first_name = user.first_name ?? user.user_details?.first_name ?? ''
          const last_name = user.last_name ?? user.user_details?.last_name ?? ''
          const employee_id = user.employee_id ?? user.user_details?.employee_id ?? ''
          const position = user.position ?? user.user_details?.position ?? ''
          const departmentIdRaw =
                  user.department_id ??
                  user.department?.id ??
                  user.user_details?.department_id ??
                    user.user_details?.department ??
                  null
          const departmentId =
            departmentIdRaw == null || departmentIdRaw === ''
              ? undefined
              : Number(departmentIdRaw)
          const divisionIdRaw =
                  user.division_id ??
                  user.division?.id ??
                  user.user_details?.division_id ??
                    user.user_details?.division ??
                  null
          const divisionId =
            divisionIdRaw == null || divisionIdRaw === ''
              ? undefined
              : Number(divisionIdRaw)
          const canViewAll = user.can_view_all ?? user.user_details?.can_view_all ?? false
          const canViewAllBool = canViewAll === true || canViewAll === '1' || canViewAll === 1
          const approverId =
            user.approver_id != null
              ? String(user.approver_id)
              : user.user_details?.approver_id != null
                ? String(user.user_details.approver_id)
                : ''

          reset({
            user_login: user.user_login || '',
            user_email: user.user_email || '',
            user_pass: '',
            employee_id,
            first_name,
            last_name,
            position,
            department: departmentId,
            division: divisionId,
            user_role: userRole || '',
            approver_id: approverId,
            user_status: statusValue,
            can_view_all: canViewAllBool,
          })

          setValue('first_name', first_name)
          setValue('last_name', last_name)
          setValue('employee_id', employee_id)
          setValue('position', position)
          setValue('user_role', userRole || '')
          setValue('approver_id', approverId)
          setValue('user_status', statusValue)
          setValue('can_view_all', canViewAllBool)
          setUserStatus(statusValue)
        } catch (error: any) {
          showError(error)
        }
      }
      fetchUser()
    }
  }, [isEditMode, userId, reset, showError, setValue, roles])

  const onSubmit = async (data: CreateUserFormData | UpdateUserFormData) => {
    setIsSubmitting(true)
    try {
      const selectedRole = roles.find((r) => r.id.toString() === data.user_role)
      if (!selectedRole) {
        showError({ message: 'Please select a valid role' })
        setIsSubmitting(false)
        return
      }

      // Backend expects user_role as a JSON object with an 'id' property
      // The 'id' must be a number (not a string) and cannot be 1 (Developer Account)
      const roleId = Number(selectedRole.id)
      if (isNaN(roleId) || roleId === 1) {
        showError({ message: 'Invalid role selected' })
        setIsSubmitting(false)
        return
      }
      
      const roleObject = { id: roleId }
      
      // Validate the role object structure
      if (!roleObject || typeof roleObject.id !== 'number' || roleObject.id === 1) {
        showError({ message: 'Invalid role data structure' })
        setIsSubmitting(false)
        return
      }

      const departmentId =
        data.department != null
          ? Number(data.department)
          : null

      const divisionId =
        data.division != null
          ? Number(data.division)
          : null

      const payload: Record<string, any> = {
        user_login: data.user_login,
        user_email: data.user_email,
        user_role: roleObject, // JSON object: {id: 3}
        employee_id: data.employee_id,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        position: data.position || '',
        department: departmentId,
        division: divisionId,
        can_view_all: data.can_view_all ?? false,
        approver_id: data.approver_id ? Number(data.approver_id) : null,
      }

      if (isEditMode) {
        const updateData = data as UpdateUserFormData
        if (updateData.user_pass && updateData.user_pass.trim() !== '') {
          payload.user_pass = updateData.user_pass
        }
        payload.user_status = userStatus
      } else {
        const createData = data as CreateUserFormData
        payload.user_pass = createData.user_pass
        payload.user_status = userStatus
      }

      if (isEditMode) {
        await apiClient.put(`/user-management/users/${userId}`, payload)
        showSuccess('Success', 'User updated successfully')
      } else {
        await apiClient.post('/user-management/users', payload)
        showSuccess('Success', 'User created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordGenerated = (password: string) => {
    setValue('user_pass', password)
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Username <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_login}>
            <Input {...register('user_login')} placeholder="e.g. johndoe" />
            <FormErrorMessage>{errors.user_login?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_email}>
            <Input {...register('user_email')} type="email" placeholder="e.g. john.doe@example.com" />
            <FormErrorMessage>{errors.user_email?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Employee ID <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.employee_id}>
            <Input {...register('employee_id')} placeholder="e.g. ALS-25-520" />
            <FormErrorMessage>{errors.employee_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            First Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.first_name}>
            <Input {...register('first_name')} placeholder="e.g. John" />
            <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Last Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.last_name}>
            <Input {...register('last_name')} placeholder="e.g. Doe" />
            <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Position 
          </FormLabel>
          <FormControl isInvalid={!!errors.position}>
            <Input {...register('position')} placeholder="e.g. Frontend Developer" />
            <FormErrorMessage>{errors.position?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Division <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.division}>
            <Controller
              name="division"
              control={control}
              render={({ field }) => {
                const selectedOption =
                  divisionSelectOptions.find((o) => o.value === Number(field.value)) ?? null

                return (
                  <ReactSelect<DivisionSelectOption, false>
                    instanceId="user-form-division"
                    options={divisionSelectOptions}
                    value={selectedOption}
                    onChange={(opt) => {
                      field.onChange(opt?.value ?? null) // 👈 stores ONLY ID
                      setDivisionSearchQuery('')
                    }}
                    inputValue={divisionSearchQuery}
                    onInputChange={(value, meta) => {
                      if (meta.action === 'input-change') setDivisionSearchQuery(value)
                    }}
                    filterOption={() => true}
                    isSearchable
                    isClearable
                    isLoading={divisionSearching}
                    placeholder="Search or select division..."
                  />
                )
              }}
            />
            <FormHelperText>
              List comes from the Division Management catalog and is saved to the user profile.
            </FormHelperText>
            <FormErrorMessage>{errors.division?.message}</FormErrorMessage>
          </FormControl>
        </Grid>
        
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Department <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.department}>
            <Controller
              name="department"
              control={control}
              render={({ field }) => {
                const selectedOption =
                  departmentSelectOptions.find((o) => o.value === Number(field.value)) ?? null

                return (
                  <ReactSelect<DepartmentSelectOption, false>
                    instanceId="user-form-department"
                    options={departmentSelectOptions}
                    value={selectedOption}
                    onChange={(opt) => {
                      field.onChange(opt?.value ?? null) // 👈 stores ONLY ID
                      setDepartmentSearchQuery('')
                    }}
                    inputValue={departmentSearchQuery}
                    onInputChange={(value, meta) => {
                      if (meta.action === 'input-change') setDepartmentSearchQuery(value)
                    }}
                    filterOption={() => true}
                    isSearchable
                    isClearable
                    isLoading={departmentSearching}
                    placeholder="Search or select department..."
                  />
                )
              }}
            />
            <FormHelperText>
              List comes from the Divisions catalog and is saved to the user profile.
            </FormHelperText>
            <FormErrorMessage>{errors.department?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Role <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_role}>
            <Select
              {...register('user_role')}
              placeholder={isLoadingRoles ? 'Loading roles...' : 'Select Role'}
              isDisabled={isLoadingRoles}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id.toString()}>
                  {role.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.user_role?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Designated Approver</FormLabel>
          <FormControl isInvalid={!!errors.approver_id}>
            <Select {...register('approver_id')} placeholder="None / Unassigned">
              {approvers.map((approver) => (
                <option key={approver.id} value={String(approver.id)}>
                  {approver.label}
                </option>
              ))}
            </Select>
            <FormHelperText>Select the default approver for this user.</FormHelperText>
            <FormErrorMessage>{errors.approver_id?.message as string}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Password {!isEditMode && (
              <Text as="span" color="red.500">
                *
              </Text>
            )}
          </FormLabel>
          <FormControl isInvalid={!!errors.user_pass} isRequired={!isEditMode}>
            <PasswordGenerator value={passwordValue || ''} onChange={handlePasswordGenerated} label="" />
            {!passwordValue && isEditMode && (
              <FormHelperText>Leave blank to keep the current password.</FormHelperText>
            )}
            <FormErrorMessage>{errors.user_pass?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Status
          </FormLabel>
          <FormControl>
            <Select
              value={userStatus}
              onChange={(e) => {
                const value = Number(e.target.value)
                setUserStatus(value)
                setValue('user_status', value, { shouldValidate: true })
              }}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
              <option value={2}>Suspended</option>
            </Select>
            <FormHelperText>Control whether this user can log in.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Can view all tickets
          </FormLabel>
          <FormControl display="flex" alignItems="center">
            <Switch
              id="can_view_all"
              isChecked={watch('can_view_all') ?? false}
              onChange={(e) => setValue('can_view_all', e.target.checked, { shouldValidate: true })}
            />
            <Text as="span" ml={2} fontSize="sm">
              Allow this user to see all tickets (not only assigned to them).
            </Text>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create User'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


